import { Animated, StyleSheet, View } from "react-native";
import { Colors, Metrics } from "../../resources";
import React, { useCallback, useEffect, useState } from "react";

import { noop } from "lodash";

interface IProps {
  height?: number;
  indeterminate?: boolean;
  indeterminateDuration?: number;
  progressDuration?: number;
  backgroundColor?: string;
  trackColor?: string;
  disabled: boolean;
  progress?: number;
  onCompletion?: () => {};
}

const SProgress: React.FC<IProps> = ({
  height = Metrics.size5,
  indeterminate = false,
  indeterminateDuration = 1100,
  progressDuration = 1100,
  backgroundColor = "red",
  trackColor = "#A6A6A6",
  disabled,
  progress = 0,
  onCompletion = noop,
}) => {
  const [timer] = useState(new Animated.Value(0));
  const [width] = useState(new Animated.Value(0));

  const indeterminateAnimation = Animated.timing(timer, {
    duration: indeterminateDuration,
    toValue: 1,
    useNativeDriver: true,
    isInteraction: false,
  });

  const startAnimation = useCallback(() => {
    if (indeterminate) {
      timer.setValue(0);
      Animated.loop(indeterminateAnimation).start();
    } else {
      Animated.timing(width, {
        duration: progressDuration,
        toValue: progress,
        useNativeDriver: true,
      }).start(() => {
        onCompletion();
      });
    }
  }, [
    indeterminate,
    indeterminateAnimation,
    onCompletion,
    progress,
    progressDuration,
    timer,
    width,
  ]);

  const stopAnimation = useCallback(() => {
    if (indeterminateAnimation) indeterminateAnimation.stop();

    Animated.timing(width, {
      duration: 200,
      toValue: 0,
      useNativeDriver: true,
      isInteraction: false,
    }).start();
  }, [indeterminateAnimation, width]);

  useEffect(() => {
    if (indeterminate || typeof progress === "number") {
      startAnimation();
    } else {
      stopAnimation();
    }
  }, [indeterminate, progress, startAnimation, stopAnimation]);

  const styleAnimation = () => {
    return indeterminate
      ? {
          transform: [
            {
              translateX: timer.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [-0.6 * 320, -0.5 * 0.8 * 320, 0.7 * 320],
              }),
            },
            {
              scaleX: timer.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.0001, 0.8, 0.0001],
              }),
            },
          ],
        }
      : {
          width: width.interpolate({
            inputRange: [0, 100],
            outputRange: ["0%", "100%"],
          }),
        };
  };

  return (
    <Animated.View
      style={[
        styles.container,
        {
          backgroundColor: disabled ? "transparent" : trackColor,
          height: height,
        },
      ]}
    >
      {!disabled ? (
        <Animated.View
          style={[
            styles.progressBar,
            {
              backgroundColor,
              borderRadius: height / 2,
              ...styleAnimation(),
            },
          ]}
        />
      ) : null}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    overflow: "hidden",
    borderRadius: 4,
  },
  progressBar: {
    flex: 1,
  },
});

export default SProgress;
